import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ActionButton } from '@/components/design-system';
import { useAllActivites } from '@/hooks/queries/useActivites';
import {
  useUpsertChargeRecurrente,
  type ChargeAllocationType,
  type ChargePeriodicite,
  type ChargeRecurrente,
} from '@/hooks/queries/useCaisseGroupe';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: ChargeRecurrente | null;
  onSaved?: () => void;
}

const PRESETS_KOST: Record<string, number> = {
  'trip-sun': 16.6,
  'kost-academy-b2b': 16.6,
  'kost-academy-b2c': 33.3,
  teleoperatrices: 16.6,
  direction: 16.6,
};

export function ChargeRecurrenteFormDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: Props): JSX.Element {
  const upsert = useUpsertChargeRecurrente();
  const { data: activites = [] } = useAllActivites();

  const [libelle, setLibelle] = useState('');
  const [description, setDescription] = useState('');
  const [allocationType, setAllocationType] = useState<ChargeAllocationType>('groupe');
  const [activiteId, setActiviteId] = useState<string>('');
  const [montant, setMontant] = useState('');
  const [periodicite, setPeriodicite] = useState<ChargePeriodicite>('mensuelle');
  const [jourDuMois, setJourDuMois] = useState('30');
  const [prochaineEcheance, setProchaineEcheance] = useState('');
  const [autoGenerer, setAutoGenerer] = useState(true);
  const [actif, setActif] = useState(true);
  const [allocPct, setAllocPct] = useState<Record<string, number>>(PRESETS_KOST);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setLibelle(editing.libelle);
      setDescription(editing.description ?? '');
      setAllocationType(editing.allocation_type);
      setActiviteId(editing.activite_id ?? '');
      setMontant(String(editing.montant));
      setPeriodicite(editing.periodicite);
      setJourDuMois(String(editing.jour_du_mois ?? 1));
      setProchaineEcheance(editing.prochaine_echeance ?? '');
      setAutoGenerer(editing.auto_generer_echeance);
      setActif(editing.actif);
      setAllocPct((editing.allocation_pct as Record<string, number>) ?? PRESETS_KOST);
    } else {
      setLibelle('');
      setDescription('');
      setAllocationType('groupe');
      setActiviteId('');
      setMontant('');
      setPeriodicite('mensuelle');
      setJourDuMois('30');
      setProchaineEcheance('');
      setAutoGenerer(true);
      setActif(true);
      setAllocPct(PRESETS_KOST);
    }
    setError(null);
  }, [open, editing]);

  const totalAlloc = useMemo(
    () => Object.values(allocPct).reduce((s, v) => s + Number(v || 0), 0),
    [allocPct],
  );

  const updateAlloc = (code: string, value: number): void => {
    setAllocPct((cur) => ({ ...cur, [code]: value }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!libelle.trim() || !montant) {
      setError('Libellé et montant obligatoires');
      return;
    }
    if (allocationType === 'groupe' && Math.abs(totalAlloc - 100) > 0.5) {
      setError(`L'allocation doit totaliser 100% (actuellement ${totalAlloc.toFixed(1)}%)`);
      return;
    }
    try {
      await upsert.mutateAsync({
        id: editing?.id,
        libelle: libelle.trim(),
        description: description.trim() || null,
        allocation_type: allocationType,
        activite_id: allocationType === 'par_activite' ? activiteId || null : null,
        allocation_pct: allocationType === 'groupe' ? allocPct : null,
        montant: Number(montant),
        periodicite,
        jour_du_mois: Number(jourDuMois) || null,
        prochaine_echeance: prochaineEcheance || null,
        auto_generer_echeance: autoGenerer,
        actif,
      });
      toast.success(editing ? 'Charge mise à jour' : 'Charge créée');
      onSaved?.();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Modifier ${editing.libelle}` : 'Nouvelle charge récurrente'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}

            <Field label="Libellé" required>
              <input
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                className="input"
                placeholder="Loyer immeuble, Salaires staff, Électricité…"
                required
              />
            </Field>

            <Field label="Description">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="input min-h-[60px]"
              />
            </Field>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Montant (DA)" required>
                <input
                  type="number"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  className="input tabular-nums"
                  required
                />
              </Field>
              <Field label="Périodicité" required>
                <select
                  value={periodicite}
                  onChange={(e) => setPeriodicite(e.target.value as ChargePeriodicite)}
                  className="input"
                >
                  <option value="mensuelle">Mensuelle</option>
                  <option value="trimestrielle">Trimestrielle</option>
                  <option value="semestrielle">Semestrielle</option>
                  <option value="annuelle">Annuelle</option>
                </select>
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Jour du mois">
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={jourDuMois}
                  onChange={(e) => setJourDuMois(e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Prochaine échéance">
                <input
                  type="date"
                  value={prochaineEcheance}
                  onChange={(e) => setProchaineEcheance(e.target.value)}
                  className="input"
                />
              </Field>
            </div>

            <Field label="Type d'allocation">
              <div className="flex gap-2">
                {(['groupe', 'par_activite'] as ChargeAllocationType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setAllocationType(t)}
                    className={
                      'flex-1 rounded-xl border-2 px-3 py-2 text-xs font-bold transition-all ' +
                      (allocationType === t
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-slate-200 bg-white text-slate-600')
                    }
                  >
                    {t === 'groupe' ? 'Groupe (allocation %)' : 'Spécifique activité'}
                  </button>
                ))}
              </div>
            </Field>

            {allocationType === 'par_activite' && (
              <Field label="Activité">
                <select
                  value={activiteId}
                  onChange={(e) => setActiviteId(e.target.value)}
                  className="input"
                >
                  <option value="">— Sélectionner —</option>
                  {activites.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {allocationType === 'groupe' && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                    Allocation par activité (%)
                  </span>
                  <span
                    className={
                      'text-xs font-bold tabular-nums ' +
                      (Math.abs(totalAlloc - 100) < 0.5 ? 'text-emerald-700' : 'text-rose-700')
                    }
                  >
                    Total : {totalAlloc.toFixed(1)}%
                  </span>
                </div>
                <div className="space-y-2 rounded-xl bg-slate-50/60 p-3">
                  {activites.map((a) => (
                    <div key={a.id} className="grid grid-cols-[1fr_auto] items-center gap-2">
                      <span className="text-xs text-slate-700">{a.nom}</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={allocPct[a.code] ?? 0}
                        onChange={(e) => updateAlloc(a.code, Number(e.target.value))}
                        className="h-8 w-24 rounded-lg border border-slate-200 bg-white px-2 text-right text-xs tabular-nums"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  checked={autoGenerer}
                  onChange={(e) => setAutoGenerer(e.target.checked)}
                />
                <span className="text-xs text-slate-700">Auto-générer échéances</span>
              </label>
              <label className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2">
                <input
                  type="checkbox"
                  checked={actif}
                  onChange={(e) => setActif(e.target.checked)}
                />
                <span className="text-xs text-slate-700">Actif</span>
              </label>
            </div>
          </DialogBody>
          <DialogFooter>
            <ActionButton type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Annuler
            </ActionButton>
            <ActionButton
              type="submit"
              variant="gradient"
              module="finances"
              loading={upsert.isPending}
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </ActionButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
